import { Appearance, StripeElementsOptionsMode } from '@stripe/stripe-js';

export type AppearenceOptions = {
	theme?: Appearance['theme'];
	variables?: Appearance['variables'];
	rules?: Partial<Record<
		| 'Label'
		| 'Input'
		| 'Input:focus'
		| 'Tab'
		| 'Tab:hover'
		| 'Tab:focus'
		| 'Tab--selected'
		| 'Tab--selected:hover'
		| 'Tab--selected:focus'
		| 'Block'
		| 'c-BlockDivider--horizontal'
		| 'RedirectText',
		{ [cssPropertyName: string]: string }
	>>;
};

export const defaultAppearenceRules = (rules?: AppearenceOptions['rules']): Appearance['rules'] => ({
	'.Label': {
		fontSize: '15px',
		marginBottom: '8px',
		...rules?.['Label'],
	},
	'.Input': {
		// SWTP-1350
		// This is a bug in all browsers when using native select input with transparent background - leeds to white background of dropdown options
		// https://codepen.io/AmeliaBR/pres/PpEPGz
		// But in stripe we dont have an css selector only for dropdown options (e.g. `.Input option`), so using `.Input` selector
		// To fix this we need to set it to solid color, in order to propagate background to dropdown options
		backgroundColor: '#1D1D1D', // same as '#FFFFFF0D',
		borderWidth: '0px',
		boxShadow: 'none',
		...rules?.['Input'],
	},
	'.Input:focus': {
		boxShadow: '0 0 0 2px #2B2D2F',
		...rules?.['Input:focus'],
	},
	'.Tab': {
		backgroundColor: '#FFFFFF0D',
		color: '#FFFFFF',
		fontSize: '13px',
		fontWeight: '500',
		lineHeight: '16px',
		border: '2px solid #FFFFFF0D',
		boxShadow: '0px 2px 4px 0px #00000012',
		...rules?.['Tab'],
	},
	'.Tab:hover': {
		borderColor: '#FFFFFF',
		boxShadow: '0px 2px 4px 0px #00000012',
		...rules?.['Tab:hover'],
	},
	'.Tab:focus': {
		borderColor: '#FFFFFF',
		boxShadow: '0px 2px 4px 0px #00000012',
		...rules?.['Tab:focus'],
	},
	'.Tab--selected': {
		color: '#FFFFFF',
		borderColor: '#FFFFFF',
		boxShadow: '0px 2px 4px 0px #00000012',
		...rules?.['Tab--selected'],
	},
	'.Tab--selected:hover': {
		color: '#FFFFFF',
		borderColor: '#FFFFFF',
		boxShadow: '0px 2px 4px 0px #00000012',
		...rules?.['Tab--selected:hover'],
	},
	'.Tab--selected:focus': {
		color: '#FFFFFF',
		borderColor: '#FFFFFF',
		boxShadow: '0px 2px 4px 0px #00000012',
		...rules?.['Tab--selected:focus'],
	},
	'.Block': {
		backgroundColor: '#00000033',
		borderRadius: '4px',
		padding: '12px',
		fontSize: '15px',
		fontWeight: '600',
		lineHeight: '21px',
		borderWidth: '0px',
		...rules?.['Block'],
	},
	'.c-BlockDivider--horizontal': {
		marginTop: '12px',
		marginBottom: '10px',
		backgroundColor: '#FFFFFF0D',
		...rules?.['c-BlockDivider--horizontal'],
	},
	'.RedirectText': {
		fontSize: '13px',
		lineHeight: '16px',
		...rules?.['RedirectText'],
	},
});

export const defaultStripeElementsOptions = (appearence?: AppearenceOptions): StripeElementsOptionsMode => ({
	mode: 'payment',
	currency: 'usd',
	payment_method_types: ['card'],
	setup_future_usage: 'off_session',
	loader: 'always',

	appearance: {
		theme: appearence?.theme || 'night',
		variables: {
			colorText: '#FFFFFF',
			colorTextPlaceholder: '#FFFFFF99',
			colorTextSecondary: '#C6C6C6',
			colorIconRedirect: '#656565',
			colorIconTab: '#FFFFFF',
			colorIconTabHover: '#FFFFFF',
			colorIconTabSelected: '#FFFFFF',
			...appearence?.variables,
		},
		rules: defaultAppearenceRules(appearence?.rules),
	},
});
